import { useState, useRef } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { lightDark, DARK } from "utils/lightDark";

import { adminApi } from "services/api";
import { useAuth } from "context/AuthContext";

// ─── helpers ──────────────────────────────────────────────────────────────────
// Small IconButtons render ~28px; give them a finger-sized target below md.
const touchIconButtonSx = ({ breakpoints }) => ({
  [breakpoints.down("md")]: { width: 36, height: 36 },
});

function getInitials(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function PasswordStrength({ password }) {
  if (!password) return null;
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const colors = ["error", "warning", "info", "success"];
  const labels = ["Weak", "Fair", "Good", "Strong"];
  return (
    <MDBox mt={0.5}>
      <LinearProgress
        variant="determinate"
        value={(score / 4) * 100}
        color={colors[score - 1] || "error"}
        sx={{ height: 4, borderRadius: 2 }}
      />
      <MDTypography variant="caption" color={colors[score - 1] || "error"} sx={{ fontWeight: 600 }}>
        {labels[score - 1] || "Too short"}
      </MDTypography>
    </MDBox>
  );
}

// ─── Section card wrapper ─────────────────────────────────────────────────────
function SectionCard({ icon, title, subtitle, children }) {
  return (
    <Card
      sx={{
        boxShadow: "0 4px 24px rgba(102,126,234,0.1)",
        border: "1px solid rgba(102,126,234,0.1)",
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      {/* Card header */}
      <MDBox
        sx={{
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(102,126,234,0.15)",
        }}
      >
        <MDBox
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(102,126,234,0.35)",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: "white", fontSize: 18 }}>{icon}</Icon>
        </MDBox>
        <MDBox>
          <MDTypography
            variant="button"
            fontWeight="bold"
            color="white"
            sx={{ display: "block", lineHeight: 1.2 }}
          >
            {title}
          </MDTypography>
          {subtitle && (
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.68rem" }}
            >
              {subtitle}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      <MDBox p={3}>{children}</MDBox>
    </Card>
  );
}

// How the account signs in, as GET /api/auth/me reports it.
const AUTH_PROVIDER_LABELS = { microsoft: "Microsoft", google: "Google", local: "Password" };

// ─── Main page ────────────────────────────────────────────────────────────────
function ProfilePage() {
  const { user, setUser } = useAuth();
  const fileInputRef = useRef();

  // Personal info form
  const [info, setInfo] = useState({
    name: user?.name || "",
    position: user?.position || "",
    bio: user?.bio || "",
    phone: user?.phone || "",
  });
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoMsg, setInfoMsg] = useState(null);

  // Photo
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoMsg, setPhotoMsg] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(user?.profileImage || null);

  // Password form
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleInfoSave = async () => {
    if (!info.name.trim()) {
      setInfoMsg({ type: "error", text: "Name is required" });
      return;
    }
    setInfoSaving(true);
    setInfoMsg(null);
    try {
      const updated = await adminApi.updateMe(info);
      setUser(updated);
      setInfoMsg({ type: "success", text: "Profile updated successfully" });
    } catch (err) {
      setInfoMsg({ type: "error", text: err.response?.data?.error || "Failed to update profile" });
    } finally {
      setInfoSaving(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (e.target.value !== undefined) e.target.value = ""; // lets the same file be picked again
    return uploadPhotoFile(file);
  };

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    if (photoUploading) return;
    return uploadPhotoFile(e.dataTransfer?.files?.[0]);
  };

  const uploadPhotoFile = async (file) => {
    if (!file) return;
    // The server only accepts JPG, PNG and GIF.
    if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
      setPhotoMsg({ type: "error", text: "Please choose a JPG, PNG or GIF image" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoMsg({ type: "error", text: "Image must be smaller than 5 MB" });
      return;
    }

    // Show local preview immediately
    const local = URL.createObjectURL(file);
    setPreviewUrl(local);
    setPhotoUploading(true);
    setPhotoMsg(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await adminApi.uploadImage(formData);
      const imageUrl = res?.file?.url || res?.file?.path || res?.url || res?.fileUrl || res?.path;
      if (!imageUrl || typeof imageUrl !== "string")
        throw new Error("Upload response did not contain a URL");
      const updated = await adminApi.updateMe({ profileImage: imageUrl });
      setUser(updated);
      setPreviewUrl(imageUrl);
      setPhotoMsg({ type: "success", text: "Photo updated" });
    } catch (err) {
      setPhotoMsg({ type: "error", text: err.response?.data?.error || "Upload failed" });
      setPreviewUrl(user?.profileImage || null);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoUploading(true);
    setPhotoMsg(null);
    try {
      const updated = await adminApi.updateMe({ profileImage: "" });
      setUser(updated);
      setPreviewUrl(null);
      setPhotoMsg({ type: "success", text: "Photo removed" });
    } catch (err) {
      setPhotoMsg({ type: "error", text: "Failed to remove photo" });
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!passwords.current || !passwords.next || !passwords.confirm) {
      setPwMsg({ type: "error", text: "All password fields are required" });
      return;
    }
    if (passwords.next !== passwords.confirm) {
      setPwMsg({ type: "error", text: "New passwords do not match" });
      return;
    }
    if (passwords.next.length < 6) {
      setPwMsg({ type: "error", text: "New password must be at least 6 characters" });
      return;
    }
    setPwSaving(true);
    setPwMsg(null);
    try {
      await adminApi.changePassword({
        currentPassword: passwords.current,
        newPassword: passwords.next,
      });
      setPasswords({ current: "", next: "", confirm: "" });
      setPwMsg({ type: "success", text: "Password changed successfully" });
    } catch (err) {
      setPwMsg({ type: "error", text: err.response?.data?.error || "Failed to change password" });
    } finally {
      setPwSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* ── Hero cover ── */}
      <MDBox
        sx={{
          height: { xs: 160, sm: 200, md: 240 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "20px",
          position: "relative",
          overflow: "hidden",
          mx: 0,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage: "radial-gradient(rgba(102,126,234,0.08) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          },
        }}
      >
        {/* Orb decorations */}
        <MDBox
          sx={{
            position: "absolute",
            top: -60,
            left: -60,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
            filter: "blur(60px)",
          }}
        />
        <MDBox
          sx={{
            position: "absolute",
            bottom: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.15)",
            filter: "blur(50px)",
          }}
        />

        {/* Bottom gradient to blend with card */}
        <MDBox
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 60,
            background: "linear-gradient(to bottom, transparent, rgba(102,126,234,0.3))",
          }}
        />
      </MDBox>

      {/* ── Profile identity card ── */}
      <MDBox sx={{ mx: { xs: 2, sm: 4 }, mt: -6, mb: 4, position: "relative", zIndex: 10 }}>
        <Card
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 16px 48px rgba(102,126,234,0.25)",
            border: "1px solid rgba(102,126,234,0.12)",
          }}
        >
          {/* Top accent strip */}
          <MDBox
            sx={{ height: 4, background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)" }}
          />

          <MDBox
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: 3,
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 3,
            }}
          >
            {/* Avatar with upload button */}
            <MDBox sx={{ position: "relative", flexShrink: 0 }}>
              <MDBox
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 80, sm: 100 },
                  borderRadius: "50%",
                  position: "relative",
                }}
              >
                {/* Glow ring */}
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: -4,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    opacity: 0.4,
                    filter: "blur(8px)",
                  }}
                />
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: -2.5,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                />
                <MDBox
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "3px solid white",
                    bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="profile"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <MDTypography
                      fontWeight="black"
                      sx={{
                        color: "white",
                        fontSize: { xs: "1.4rem", sm: "1.8rem" },
                        lineHeight: 1,
                      }}
                    >
                      {getInitials(user?.name, user?.email)}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>

              {/* Camera button */}
              <Tooltip title="Change photo">
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading}
                  sx={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    width: { xs: 36, md: 30 },
                    height: { xs: 36, md: 30 },
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    color: "white",
                    boxShadow: "0 3px 10px rgba(102,126,234,0.5)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5568d3, #623d89)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {photoUploading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Icon sx={{ fontSize: "1rem !important" }}>photo_camera</Icon>
                  )}
                </IconButton>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                style={{ display: "none" }}
                onChange={handlePhotoChange}
              />
            </MDBox>

            {/* Identity text */}
            <MDBox flex={1} minWidth={0}>
              <MDTypography
                variant="h4"
                fontWeight="bold"
                sx={{ lineHeight: 1.2, mb: 0.5, color: lightDark("#344767", DARK.text) }}
              >
                {user?.name || "—"}
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mb: 1.5 }}>
                {user?.email}
              </MDTypography>
              <MDBox display="flex" flexWrap="wrap" gap={1}>
                {user?.position && (
                  <Chip
                    icon={<Icon sx={{ fontSize: "0.85rem !important" }}>work</Icon>}
                    label={user.position}
                    size="small"
                    sx={{
                      bgcolor: "rgba(102,126,234,0.1)",
                      color: lightDark("#4a5ad1", "#aab4ff"),
                      border: "1px solid rgba(102,126,234,0.25)",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                    }}
                  />
                )}
                <Chip
                  icon={<Icon sx={{ fontSize: "0.85rem !important" }}>shield</Icon>}
                  label={user?.isGlobalAdmin ? "Global Admin" : user?.role || "User"}
                  size="small"
                  sx={{
                    bgcolor: user?.isGlobalAdmin ? "rgba(76,175,80,0.1)" : "rgba(123,128,154,0.1)",
                    color: user?.isGlobalAdmin
                      ? lightDark("#2e7d32", "#81c784")
                      : lightDark("#7b809a", DARK.textMuted),
                    border: `1px solid ${
                      user?.isGlobalAdmin ? "rgba(76,175,80,0.25)" : "rgba(123,128,154,0.25)"
                    }`,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                  }}
                />
              </MDBox>
            </MDBox>

            {/* Member since */}
            <MDBox sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0 }}>
              <MDTypography
                variant="caption"
                color="text"
                sx={{
                  opacity: 0.6,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontSize: "0.62rem",
                  display: "block",
                }}
              >
                Member since
              </MDTypography>
              <MDTypography variant="body2" fontWeight="bold" color="text">
                {user?.createdAt
                  ? new Date(user.createdAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* ── Content ── */}
      <MDBox px={{ xs: 2, sm: 4 }} pb={4}>
        <Grid container spacing={3}>
          {/* Left column */}
          <Grid item xs={12} lg={7}>
            {/* Personal information */}
            <SectionCard
              icon="person"
              title="Personal Information"
              subtitle="Update your name, position and bio"
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Full name"
                    fullWidth
                    size="small"
                    value={info.name}
                    onChange={(e) => setInfo((f) => ({ ...f, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Position / job title"
                    fullWidth
                    size="small"
                    placeholder="e.g. Software Developer"
                    value={info.position}
                    onChange={(e) => setInfo((f) => ({ ...f, position: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone number"
                    fullWidth
                    size="small"
                    placeholder="+44 7911 123456"
                    value={info.phone}
                    onChange={(e) => setInfo((f) => ({ ...f, phone: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email address"
                    fullWidth
                    size="small"
                    value={user?.email || ""}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Bio"
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder="Tell the team a bit about yourself…"
                    value={info.bio}
                    onChange={(e) => setInfo((f) => ({ ...f, bio: e.target.value }))}
                  />
                </Grid>
              </Grid>

              {infoMsg && (
                <Alert severity={infoMsg.type} sx={{ mt: 2.5, borderRadius: "10px" }}>
                  {infoMsg.text}
                </Alert>
              )}

              <MDBox mt={2.5} display="flex" justifyContent="flex-end">
                <MDButton
                  variant="contained"
                  onClick={handleInfoSave}
                  disabled={infoSaving}
                  startIcon={
                    infoSaving ? <CircularProgress size={16} color="inherit" /> : <Icon>save</Icon>
                  }
                  sx={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    borderRadius: "10px",
                    fontWeight: 700,
                    px: 3,
                    boxShadow: "0 4px 14px rgba(102,126,234,0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5568d3 0%, #623d89 100%)",
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 20px rgba(102,126,234,0.4)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  Save Changes
                </MDButton>
              </MDBox>
            </SectionCard>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} lg={5}>
            {/* Photo upload */}
            <MDBox mb={3}>
              <SectionCard
                icon="photo_camera"
                title="Profile Photo"
                subtitle="JPG, PNG or GIF · max 5 MB"
              >
                {/* Drop zone */}
                <MDBox
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handlePhotoDrop}
                  sx={{
                    border: "2px dashed",
                    borderColor: photoUploading
                      ? "primary.main"
                      : lightDark("grey.300", "rgba(255, 255, 255, 0.24)"),
                    borderRadius: "14px",
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    bgcolor: photoUploading
                      ? "rgba(102,126,234,0.04)"
                      : lightDark("grey.50", DARK.surface),
                    "&:hover": { borderColor: "primary.main", bgcolor: "rgba(102,126,234,0.06)" },
                    mb: previewUrl ? 2 : 0,
                  }}
                >
                  {photoUploading ? (
                    <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <CircularProgress size={32} />
                      <MDTypography variant="caption" color="text">
                        Uploading…
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <>
                      <Icon sx={{ fontSize: "2.5rem !important", color: "grey.400", mb: 0.5 }}>
                        cloud_upload
                      </Icon>
                      <MDTypography variant="body2" fontWeight="bold" color="text">
                        Click to upload a photo
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                        or drag and drop here
                      </MDTypography>
                    </>
                  )}
                </MDBox>

                {/* Current photo preview */}
                {previewUrl && (
                  <MDBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      borderRadius: "12px",
                      border: "1px solid",
                      borderColor: lightDark("grey.200", DARK.border),
                      bgcolor: lightDark("grey.50", DARK.surface),
                    }}
                  >
                    <img
                      src={previewUrl}
                      alt="Current"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #e0e0e0",
                      }}
                    />
                    <MDBox flex={1} minWidth={0}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        color="text"
                        sx={{ display: "block" }}
                      >
                        Current photo
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.6, fontSize: "0.65rem" }}
                      >
                        Click the zone above to replace
                      </MDTypography>
                    </MDBox>
                    <Tooltip title="Remove photo">
                      <IconButton
                        size="small"
                        color="error"
                        sx={touchIconButtonSx}
                        onClick={handleRemovePhoto}
                        disabled={photoUploading}
                      >
                        <Icon sx={{ fontSize: "1rem !important" }}>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                )}

                {photoMsg && (
                  <Alert severity={photoMsg.type} sx={{ mt: 2, borderRadius: "10px" }}>
                    {photoMsg.text}
                  </Alert>
                )}
              </SectionCard>
            </MDBox>

            {/* Change password — only accounts that have one. Microsoft and Google accounts
                manage theirs with that provider, so they get a note instead of a form that can't work. */}
            {user?.hasPassword ? (
              <SectionCard
                icon="lock"
                title="Change Password"
                subtitle="Use a strong password with mixed characters"
              >
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Current password"
                    type={showPw.current ? "text" : "password"}
                    fullWidth
                    size="small"
                    value={passwords.current}
                    onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          size="small"
                          sx={touchIconButtonSx}
                          onClick={() => setShowPw((s) => ({ ...s, current: !s.current }))}
                        >
                          <Icon sx={{ fontSize: "1.1rem !important" }}>
                            {showPw.current ? "visibility_off" : "visibility"}
                          </Icon>
                        </IconButton>
                      ),
                    }}
                  />

                  <MDBox>
                    <TextField
                      label="New password"
                      type={showPw.next ? "text" : "password"}
                      fullWidth
                      size="small"
                      value={passwords.next}
                      onChange={(e) => setPasswords((p) => ({ ...p, next: e.target.value }))}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            size="small"
                            sx={touchIconButtonSx}
                            onClick={() => setShowPw((s) => ({ ...s, next: !s.next }))}
                          >
                            <Icon sx={{ fontSize: "1.1rem !important" }}>
                              {showPw.next ? "visibility_off" : "visibility"}
                            </Icon>
                          </IconButton>
                        ),
                      }}
                    />
                    <PasswordStrength password={passwords.next} />
                  </MDBox>

                  <TextField
                    label="Confirm new password"
                    type={showPw.confirm ? "text" : "password"}
                    fullWidth
                    size="small"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
                    error={!!passwords.confirm && passwords.confirm !== passwords.next}
                    helperText={
                      passwords.confirm && passwords.confirm !== passwords.next
                        ? "Passwords don't match"
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          size="small"
                          sx={touchIconButtonSx}
                          onClick={() => setShowPw((s) => ({ ...s, confirm: !s.confirm }))}
                        >
                          <Icon sx={{ fontSize: "1.1rem !important" }}>
                            {showPw.confirm ? "visibility_off" : "visibility"}
                          </Icon>
                        </IconButton>
                      ),
                    }}
                  />
                </MDBox>

                {pwMsg && (
                  <Alert severity={pwMsg.type} sx={{ mt: 2, borderRadius: "10px" }}>
                    {pwMsg.text}
                  </Alert>
                )}

                <MDBox mt={2.5} display="flex" justifyContent="flex-end">
                  <MDButton
                    variant="contained"
                    onClick={handlePasswordSave}
                    disabled={pwSaving}
                    startIcon={
                      pwSaving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Icon>lock_reset</Icon>
                      )
                    }
                    sx={{
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      borderRadius: "10px",
                      fontWeight: 700,
                      px: 3,
                      boxShadow: "0 4px 14px rgba(102,126,234,0.3)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5568d3 0%, #623d89 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 20px rgba(102,126,234,0.4)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    Update Password
                  </MDButton>
                </MDBox>
              </SectionCard>
            ) : (
              <SectionCard icon="lock" title="Password" subtitle="Managed by your sign-in provider">
                <MDTypography variant="body2" color="text">
                  You sign in with{" "}
                  {AUTH_PROVIDER_LABELS[user?.authProvider] || "Microsoft or Google"}, so
                  there&apos;s no password to change here. Change it in your{" "}
                  {AUTH_PROVIDER_LABELS[user?.authProvider] || "Microsoft or Google"} account
                  instead.
                </MDTypography>
              </SectionCard>
            )}
          </Grid>

          {/* Account info strip */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: "16px",
                border: "1px solid rgba(102,126,234,0.1)",
                boxShadow: "none",
                background: lightDark(
                  "linear-gradient(135deg, #f8f9fc 0%, #eef0f8 100%)",
                  DARK.paper
                ),
              }}
            >
              <MDBox
                sx={{
                  px: 3,
                  py: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {[
                  {
                    icon: "badge",
                    label: "Account ID",
                    value: user?._id?.slice(-8)?.toUpperCase() || "—",
                  },
                  { icon: "manage_accounts", label: "Role", value: user?.role || "—" },
                  {
                    icon: "login",
                    label: "Signs in with",
                    value:
                      AUTH_PROVIDER_LABELS[user?.authProvider] ||
                      (user?.hasPassword ? "Password" : "—"),
                  },
                  {
                    icon: "calendar_today",
                    label: "Joined",
                    value: user?.createdAt
                      ? new Date(user.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })
                      : "—",
                  },
                ].map(({ icon, label, value }) => (
                  <MDBox key={label} display="flex" alignItems="center" gap={1.5}>
                    <MDBox
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: "rgba(102,126,234,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ fontSize: "1rem !important", color: "#667eea" }}>{icon}</Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.62rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          display: "block",
                        }}
                      >
                        {label}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        color="text"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {value}
                      </MDTypography>
                    </MDBox>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mx: 0.5, display: { xs: "none", sm: "block" } }}
                    />
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default ProfilePage;
